'use client';

import React, { useState } from 'react';
import { format } from 'date-fns';
import { useRouter } from 'next/navigation';

import { Card, Separator, Skeleton } from '@mokoboko/components';
import { CreditCard, Gift, Wallet, Calendar, RefreshCw } from 'lucide-react';
import {
  giftSpendDisplay,
  wholeOrderGiftBalanceLine,
} from '@/helpers/gift-card';
import { formatAmount, formatCycles, formatRecurringPrice } from '@/lib/utils';
import { Order } from '@/lib/types';
import { formatClassCount } from '@/helpers/credits';
import { isSinglePayment } from '@/helpers/subscription';
import { formatClassesLine } from '@/helpers/subscription-plans';
import type { Subscription } from '@/state/subscription-groups';
import { useAuth } from '@/contexts/auth';
import { LoginDialog } from '@/components/dialogs/login';
import { CreateAccountDialog } from '@/components/dialogs/create-account';
import {
  PostPurchaseActions,
  PostPurchaseActionsSkeleton,
} from './post-purchase-actions';
import {
  ReceiptEmailRow,
  ReceiptRow,
  ReceiptRowSkeleton,
  ReceiptStatusHeader,
} from './receipt-rows';

// A one-payment plan's end date has a row of its own, so its classes name no
// length here.
const receiptClassesText = (subscription: Subscription | undefined): string => {
  if (!subscription) return '';
  const classes = subscription.total_number_of_classes_per_cycle;
  if (classes === 0) return '';
  if (!isSinglePayment(subscription)) {
    return formatClassesLine(classes, subscription);
  }
  return classes == null ? 'Unlimited classes' : formatClassCount(classes);
};

export const SubscriptionOrderReceipt = ({ order }: { order: Order }) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const [activeModal, setActiveModal] = useState<
    'login' | 'createAccount' | null
  >(null);

  const {
    subscription_order_items = [],
    user,
    coupon_code,
    coupon_type,
    final_amount,
    total_amount,
    currency,
    admin_user_id,
  } = order;

  const firstItem = subscription_order_items[0];
  const subscription = firstItem?.subscription;
  const subscriptionCode = firstItem?.subscription_code;

  const isAdminAdded =
    coupon_type === 'FREE' &&
    final_amount !== undefined &&
    final_amount !== null &&
    final_amount === 0 &&
    admin_user_id !== null;

  // A money card paid all or part of the first payment (gift cards M1, D306):
  // a payment, never free and never a discount, and the renewals are charged
  // in full.
  const giftSpend = order.gift_card ?? null;
  const giftSpendLines = giftSpend && giftSpendDisplay(giftSpend, currency);
  const giftBalanceLine =
    giftSpendLines &&
    wholeOrderGiftBalanceLine(giftSpendLines, order.payment_status);

  const showPaid =
    !isAdminAdded &&
    !giftSpend &&
    final_amount !== undefined &&
    final_amount !== null &&
    final_amount > 0;

  const showFree =
    !isAdminAdded &&
    !giftSpend &&
    final_amount !== undefined &&
    final_amount !== null &&
    final_amount === 0 &&
    coupon_type !== 'FREE';

  const singlePayment = isSinglePayment(subscription);
  // A €0 plan with no code is never sent to Stripe, so nothing renews it.
  const isFreePlan = !subscription?.price && !coupon_code;
  const creditsText = receiptClassesText(subscription);

  const endDate = subscriptionCode?.current_period_end
    ? format(new Date(subscriptionCode.current_period_end), 'd MMM, yyyy')
    : null;

  // No catalogue price means we cannot say what the next charge is, and this
  // row is a money promise — so drop it rather than assert "Free on <date>".
  const nextPaymentText =
    subscription?.price && endDate && !singlePayment
      ? `${formatRecurringPrice(
          subscription.price,
          subscription.currency ?? order.currency,
          subscription.charge_every_count,
          subscription.charge_every_interval,
          { shorten: true }
        )} on ${endDate}`
      : null;

  const cyclesLabel = subscription?.number_of_billing_cycles
    ? `${formatCycles(subscription.number_of_billing_cycles)}, then ends`
    : 'Until you cancel';

  return (
    <>
      <div className="bg-white">
        <ReceiptStatusHeader
          status="confirmed"
          statusText="Membership active"
          title={subscription?.name ?? 'Membership'}
        />

        <Separator className="my-5 sm:my-6" />

        <div className="flex flex-col gap-4">
          {creditsText && (
            <ReceiptRow icon={Wallet} title={creditsText}>
              <p className="mt-1 text-sm text-gray-600">
                {giftSpend ? (
                  formatAmount(total_amount, currency)
                ) : showFree ? (
                  'Free'
                ) : showPaid ? (
                  <>
                    Paid:{' '}
                    {final_amount !== total_amount && (
                      <>
                        <span className="text-gray-400 line-through">
                          {formatAmount(total_amount, currency)}
                        </span>{' '}
                      </>
                    )}
                    {formatAmount(final_amount, currency)}
                    {order.discount_amount > 0 && (
                      <>
                        {' · '}
                        {order.discount_percentage > 0
                          ? `${order.discount_percentage}% off`
                          : `${formatAmount(order.discount_amount, currency)} off`}
                        {coupon_code && ` (${coupon_code})`}
                      </>
                    )}
                  </>
                ) : null}
              </p>
            </ReceiptRow>
          )}

          {giftSpendLines && (
            <ReceiptRow icon={Gift} title={`Gift card ${giftSpendLines.applied}`}>
              {giftBalanceLine && (
                <p className="mt-1 text-sm text-gray-600">{giftBalanceLine}</p>
              )}
            </ReceiptRow>
          )}

          {giftSpend && giftSpend.remainder > 0 && giftSpendLines && (
            <ReceiptRow icon={CreditCard} title={giftSpendLines.remainder}>
              <p className="mt-1 text-sm text-gray-600">Paid</p>
            </ReceiptRow>
          )}

          {singlePayment && endDate && (
            <ReceiptRow icon={Calendar} title={`Ends ${endDate}`}>
              <p className="mt-1 text-sm text-gray-600">
                One-time payment, no renewal
              </p>
            </ReceiptRow>
          )}

          {nextPaymentText && !isAdminAdded && (
            <ReceiptRow icon={Calendar} title={nextPaymentText}>
              <p className="mt-1 text-sm text-gray-600">Next payment</p>
            </ReceiptRow>
          )}

          {!isAdminAdded &&
            subscriptionCode &&
            !singlePayment &&
            !isFreePlan && (
              <ReceiptRow icon={RefreshCw} title="Renewal: On">
                <p className="mt-1 text-sm text-gray-600">{cyclesLabel}</p>
              </ReceiptRow>
            )}

          {user?.email && <ReceiptEmailRow email={user.email} />}
        </div>

        <Separator className="my-6" />

        <PostPurchaseActions
          isAuthenticated={isAuthenticated}
          onLoginClick={() => setActiveModal('login')}
          onBookClass={() => router.push('/classes')}
          secondaryLabel={isAuthenticated ? 'Manage membership' : 'My account'}
          onSecondaryClick={
            isAuthenticated
              ? () => router.push('/dashboard/credits')
              : () => setActiveModal('login')
          }
        />
      </div>

      {activeModal === 'login' && (
        <LoginDialog
          open={activeModal === 'login'}
          onClose={() => setActiveModal(null)}
          onConfirm={() => setActiveModal(null)}
          onCreateAccountLinkClick={() => setActiveModal('createAccount')}
        />
      )}

      {activeModal === 'createAccount' && (
        <CreateAccountDialog
          open={activeModal === 'createAccount'}
          onClose={() => setActiveModal(null)}
          onLoginLinkClick={() => setActiveModal('login')}
          onConfirm={async () => setActiveModal(null)}
        />
      )}
    </>
  );
};

export const SubscriptionReceiptSkeleton = () => {
  return (
    <Card contentClassName="p-5 sm:p-6 flex flex-col gap-5 sm:gap-6">
      <div className="flex items-center justify-between gap-4">
        <Skeleton isRounded className="!h-11 w-[140px] sm:!h-10 sm:w-[124px]" />
      </div>

      <Separator />

      <div className="flex flex-col items-center">
        <Skeleton isCircle height={80} />
        <div className="mt-3">
          <Skeleton text="body-sm" width={128} />
        </div>
        <div className="mt-1.5">
          <Skeleton text="h2" width={176} />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <ReceiptRowSkeleton key={i} />
        ))}
      </div>

      <Separator />

      <PostPurchaseActionsSkeleton />
    </Card>
  );
};
